import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Chip,
    Snackbar,
    Divider,
    Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SaveIcon from "@mui/icons-material/Save";
import MainCard from "../../components/cards/MainCard";
import { getCardColor } from "../../utils/colorUtils";
import { useGetDmiSettingsQuery, useUpdateDmiSettingsMutation } from "../../store/services/tenancy.service";

export default function AzureSimInjector(props) {
    const { data } = props;
    const tenancyId = data?.tenancyId;
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const [functionAppUrl, setFunctionAppUrl] = useState('');
    const [apiKey, setApiKey] = useState('');

    const [testStatus, setTestStatus] = useState(null);
    const [testMessage, setTestMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');

    const { data: dmiSettings, isLoading: dmiLoading } = useGetDmiSettingsQuery(
        { tenancyId },
        { skip: !tenancyId }
    );
    const [updateDmiSettings, { isLoading: saving }] = useUpdateDmiSettingsMutation();

    useEffect(() => {
        if (dmiSettings) {
            if (dmiSettings.dmiFunctionUrl) setFunctionAppUrl(dmiSettings.dmiFunctionUrl);
            if (dmiSettings.dmiApiKey) setApiKey(dmiSettings.dmiApiKey);
        }
    }, [dmiSettings]);

    const handleSaveConfig = async () => {
        if (!tenancyId) return;

        try {
            await updateDmiSettings({
                tenancyId,
                data: {
                    dmiFunctionUrl: functionAppUrl,
                    dmiApiKey: apiKey,
                }
            }).unwrap();

            setSnackbarSeverity('success');
            setSnackbarMessage('DMI configuration saved successfully.');
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarSeverity('error');
            setSnackbarMessage('Failed to save DMI configuration.');
            setSnackbarOpen(true);
        }
    };

    const handleTestConnection = async () => {
        if (!functionAppUrl || !apiKey) return;

        setTestStatus('loading');
        setTestMessage('');

        try {
            const url = functionAppUrl.endsWith('/')
                ? functionAppUrl.slice(0, -1)
                : functionAppUrl;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey,
                },
                body: JSON.stringify({
                    toAddress: 'permissiontest@boxphish-verification.invalid',
                    fromAddress: 'test@boxphish.com',
                    fromDisplayName: 'Boxphish Permission Test',
                    subject: 'Permission Verification',
                    body: '<p>This is a permission verification test.</p>',
                }),
            });

            if (response.status === 401) {
                setTestStatus('auth_fail');
                setTestMessage('API key authentication failed. The function returned 401 — check that the API key matches what was set during deployment.');
                return;
            }

            if (response.status === 200) {
                setTestStatus('success');
                setTestMessage('Connection verified and permissions are working. The function successfully authenticated with Microsoft Graph.');
                return;
            }

            const responseData = await response.json().catch(() => ({}));
            const errorMsg = responseData.error || '';

            if (response.status === 500 && (
                errorMsg.includes('does not exist') ||
                errorMsg.includes('not found') ||
                errorMsg.includes('ResourceNotFound') ||
                errorMsg.includes('Request_ResourceNotFound') ||
                errorMsg.includes('MailboxNotFound')
            )) {
                setTestStatus('success');
                setTestMessage('Connection verified. The function is reachable, API key is valid, and the Managed Identity has Graph API access. The test used a dummy address so the "user not found" error is expected.');
                return;
            }

            if (response.status === 500 && (
                errorMsg.includes('Authorization') ||
                errorMsg.includes('Forbidden') ||
                errorMsg.includes('Access') ||
                errorMsg.includes('Insufficient privileges') ||
                errorMsg.includes('UnknownError') ||
                errorMsg.includes('401') ||
                errorMsg.includes('403')
            )) {
                setTestStatus('permission_fail');
                setTestMessage('The function is reachable and authenticated, but the Managed Identity does not have the Mail.ReadWrite permission yet. Please grant the permission via the Azure Portal tab, then test again.');
                return;
            }

            setTestStatus('error');
            setTestMessage(`Function returned ${response.status}: ${errorMsg || 'Unknown error'}`);

        } catch (err) {
            setTestStatus('error');
            setTestMessage(
                err.message.includes('Failed to fetch') || err.message.includes('NetworkError')
                    ? 'Could not reach the Function App. Check the URL is correct, the function is deployed, and CORS is enabled.'
                    : `Connection error: ${err.message}`
            );
        }
    };

    const testResultSeverity = {
        success: 'success',
        auth_fail: 'error',
        permission_fail: 'warning',
        error: 'error',
    };

    const hasUnsavedChanges = dmiSettings && (
        functionAppUrl !== (dmiSettings.dmiFunctionUrl || '') ||
        apiKey !== (dmiSettings.dmiApiKey || '')
    );

    return (
        <>
            <Grid container spacing={2}>
                {/* Step 1: Deploy */}
                <Grid item xs={12}>
                    <MainCard content boxShadow border={false} sx={{ bgcolor: getCardColor(lightMode) }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="h4">
                                Simulation Email Injector
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                            Deploy an Azure Function App into your Microsoft 365 tenant to enable phishing simulation email injection via the Graph API.
                            The function uses a system-assigned Managed Identity — no credentials are stored.
                        </Typography>

                        {/* Step 1: Run Installer */}
                        <MainCard content boxShadow border={false} sx={{ bgcolor: 'background.default', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h5">
                                    Step 1: Run the Installer
                                </Typography>
                                <Chip label="Required" size="small" color="primary" variant="outlined" />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Download and run the installer script for your platform. It will deploy the Azure Function App,
                                grant the required permissions, and provide you with the Function URL and API Key.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CloudDownloadIcon />}
                                    onClick={() => {
                                        const baseUrl = window.location.origin;
                                        const batContent = `@echo off\r\necho Starting Boxphish DMI Installer...\r\necho.\r\nset "PS_SCRIPT=%TEMP%\\boxphish-dmi-installer.ps1"\r\npowershell.exe -ExecutionPolicy Bypass -NoProfile -Command "Invoke-WebRequest -Uri '${baseUrl}/boxphish-dmi-installer.ps1' -OutFile '%PS_SCRIPT%'"\r\nif not exist "%PS_SCRIPT%" (\r\n    echo ERROR: Failed to download installer script.\r\n    echo Please check your internet connection and try again.\r\n    echo.\r\n    pause\r\n    exit /b 1\r\n)\r\npowershell.exe -ExecutionPolicy Bypass -NoProfile -File "%PS_SCRIPT%"\r\ndel "%PS_SCRIPT%" >nul 2>&1\r\necho.\r\npause\r\n`;
                                        const blob = new Blob([batContent], { type: 'application/bat' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'Install-Boxphish-DMI.bat';
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                >
                                    Windows Installer (.bat)
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CloudDownloadIcon />}
                                    href="/boxphish-dmi-installer.sh"
                                    download="boxphish-dmi-installer.sh"
                                >
                                    Mac / Linux Installer (.sh)
                                </Button>
                            </Box>
                        </MainCard>

                        {/* Step 2: Enter deployment details */}
                        <MainCard content boxShadow border={false} sx={{ bgcolor: 'background.default', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h5">
                                    Step 2: Enter Deployment Details
                                </Typography>
                                {hasUnsavedChanges && (
                                    <Chip label="Unsaved changes" size="small" color="warning" variant="outlined" />
                                )}
                                {!hasUnsavedChanges && (
                                    <Chip label="Required" size="small" color="primary" variant="outlined" />
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                After running the installer, enter the Function URL and API Key shown at the end of the script.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    label="Function App URL"
                                    size="small"
                                    fullWidth
                                    value={functionAppUrl}
                                    onChange={(e) => setFunctionAppUrl(e.target.value.trim())}
                                    placeholder="https://boxphish-sim-injector.azurewebsites.net/api/inject-simulation"
                                />
                                <TextField
                                    label="API Key"
                                    size="small"
                                    fullWidth
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="Paste the API key from the installer output"
                                />
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                onClick={handleSaveConfig}
                                disabled={!functionAppUrl || !apiKey || saving || !tenancyId}
                            >
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </Button>
                        </MainCard>

                        {/* Step 3: Test Connection */}
                        <MainCard content boxShadow border={false} sx={{ bgcolor: 'background.default' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h5">
                                    Step 3: Test Connection
                                </Typography>
                                <Chip label="Optional" size="small" color="default" variant="outlined" />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                After saving your configuration, test the connection to confirm everything is working.
                            </Typography>

                            <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                                The test uses a non-existent address. A "user not found" error from Graph actually means <strong>success</strong> — it
                                proves the function is reachable, the API key works, and the Managed Identity has a valid Graph token.
                            </Alert>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleTestConnection}
                                disabled={!functionAppUrl || !apiKey || testStatus === 'loading'}
                                startIcon={testStatus === 'loading' ? <CircularProgress size={18} color="inherit" /> : null}
                                sx={{ mb: 1 }}
                            >
                                {testStatus === 'loading' ? 'Testing...' : 'Test Connection'}
                            </Button>

                            {(!functionAppUrl || !apiKey) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Save the Function App URL and API Key in Step 2 first.
                                </Typography>
                            )}

                            {testStatus && testStatus !== 'loading' && (
                                <Alert
                                    severity={testResultSeverity[testStatus]}
                                    icon={testStatus === 'success'
                                        ? <CheckCircleOutlineIcon />
                                        : <ErrorOutlineIcon />
                                    }
                                    sx={{ mt: 1.5 }}
                                    action={
                                        testStatus === 'success' ? (
                                            <Chip label="Ready" color="success" size="small" variant="outlined" />
                                        ) : null
                                    }
                                >
                                    {testMessage}
                                </Alert>
                            )}
                        </MainCard>
                    </MainCard>
                </Grid>
            </Grid>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
